/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faCaretDown,
  faCaretUp,
  faCircleXmark,
  faHand,
  faPlus,
  faSave,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import contactUUID from "./contactUUID";
import ContactFieldEditor from "./ContactFieldEditor";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import ManageFieldIcon from "../../assets/icons/Icon_Library/application icons/general/manage_fields.svg2";
import PageHeader from "../Common/PageHeader";
import {
  useGetContactFieldsQuery,
  useUpsertContactFieldsMutation,
} from "../store/slices/contactsSlice";
import { isUserPermitted } from "../helpers/pageProps";
import SelectableContactFieldError from "./SelectableContactFieldError";

const emptyContactField = () => ({
  uuid: contactUUID(),
  label: "New contact field",
  isSms: false,
  isEmail: false,
  isVoice: false,
  isDateTime: false,
  dateTimeType: "dateTime",
  isSelect: false,
  selectType: "",
  options: [],
  isPrimary: false,
  isMultiple: false,
  isUnique: false,
  minItemsRequired: 0,
  isCard: false,
  isHidden: false,
  isNew: true,
});

export default function ContactFields() {
  const [contactFields, setContactFields] = useState([]);
  const { data: fields } = useGetContactFieldsQuery();
  useEffect(() => {
    if (fields) {
      setContactFields(fields);
    }
  }, [fields]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isUserError, setIsUserError] = useState(false);
  const [isDraggable, setIsDraggable] = useState(false);
  const fieldsRef = useRef({});

  const handleChange = (contactField) => {
    setContactFields((prev) =>
      prev.map((cf) => {
        if (cf.uuid === contactField.uuid) {
          return contactField;
        }
        return cf;
      })
    );
  };

  const handleAdd = () =>
    setContactFields((prev) => [...prev, emptyContactField()]);

  const handleDeleteToggle = (uuid) =>
    setContactFields((prev) =>
      prev.map((f) => {
        if (f.uuid === uuid) {
          return { ...f, deleted: !f.deleted };
        }
        return f;
      })
    );

  const handleUp = (order) => {
    setContactFields((prev) => [
      ...prev.slice(0, order - 1),
      prev[order],
      prev[order - 1],
      ...prev.slice([order + 1]),
    ]);
  };

  const handleDown = (order) => {
    setContactFields((prev) => [
      ...prev.slice(0, order),
      prev[order + 1],
      prev[order],
      ...prev.slice([order + 2]),
    ]);
  };

  const [saveFields] = useUpsertContactFieldsMutation();
  const handleSave = async () => {
    setLoading(true);

    try {
      const body = {
        fields: contactFields
          .filter((cf) => !cf.deleted)
          .map((cf, idx) => ({ ...cf, order: idx })),
      };

      const response = await saveFields(body);

      if (response.error) {
        const errorMessage =
          response.error?.data?.message ||
          response.error?.error ||
          "Unable to save contact fields";
        toast.error(errorMessage);
        return;
      }

      const data = response.data || {};
      setErrors(data.errors || []);
      setIsUserError(data.isUserError);

      if (data.isError || data.errors?.length > 0) {
        toast.error(data.message || "There are errors");
      } else {
        toast.success("Saved succesfully");
      }
    } catch (error) {
      toast.error("Unable to save contact fields");
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (from, to) => {
    setContactFields((prev) => {
      const newFields = [...prev];
      newFields.splice(to, 0, ...newFields.splice(from, 1));
      return newFields;
    });
  };

  return (
    <div className="h-100 d-flex flex-column position-relative">
      <FullScreenLoadingIndicator loading={loading} />
      <PageHeader
        Icon={ManageFieldIcon}
        heading="Contact fields"
        subheading="Add or amend you contact fields."
      >
        <div className="d-flex flex-wrap gap-2">
          {/* <WhiteButton
            as="RRLink"
            to="/assets/contacts/bulk"
            icon={ContactsIcon}
            heading="Bulk"
            subheading="Manage"
          />
          <WhiteButton
            as="button"
            icon={ContactsIcon}
            heading="Import"
            subheading="Contacts"
          />
          <WhiteButton
            as="RRLink"
            to="/assets/contacts/add"
            icon={ContactsIcon}
            heading="Add"
            subheading="Contact"
          /> */}
        </div>
      </PageHeader>
      {!contactFields.some((cf) => cf.isSms && cf.isPrimary) && (
        <div className="alert alert-warning text-center mt-3">
          You need to have at least one primary SMS field
        </div>
      )}
      {!contactFields.some((cf) => cf.isEmail && cf.isPrimary) && (
        <div className="alert alert-warning text-center mt-3">
          You need to have at least one primary Email field
        </div>
      )}

      {!isUserPermitted("edit_system_setting") && (
        <div className="alert alert-warning text-center mt-3">
          You do not have permission to edit contact fields.
        </div>
      )}

      <fieldset disabled={!isUserPermitted("edit_system_setting")}>
        <div className="flex-grow-1">
          <div>
            {contactFields.map((contactField, index) => (
              <div
                ref={(el) => {
                  fieldsRef.current[index] = el;
                }}
                className="card bg-purple-gradient mb-4"
                key={contactField.uuid}
                draggable={isDraggable}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "copyMove";
                  e.dataTransfer.setData("text/plain", index);
                  setIsDraggable(false);
                }}
                onDragOver={(e) => {
                  if (e.dataTransfer.getData("text/plain") != index) {
                    e.dataTransfer.dropEffect = "copy";
                    e.preventDefault();
                    fieldsRef.current[index].classList.add("border-purple");
                  }
                }}
                onDragExit={() =>
                  fieldsRef.current[index].classList.remove("border-purple")
                }
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(e.dataTransfer.getData("text/plain"), index);
                  fieldsRef.current[index].classList.remove("border-purple");
                }}
              >
                {contactField.deleted && (
                  <div className="position-absolute w-100 h-100 bg-secondary bg-opacity-75 rounded-1 z-2">
                    <div className="position-absolute top-50 start-50 translate-middle fs-1 text-white fw-bold">
                      <div>
                        This field will be deleted!
                        <button
                          type="button"
                          className="btn btn-primary rounded-1"
                          onClick={() => handleDeleteToggle(contactField.uuid)}
                        >
                          undo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="card-header d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="mx-2">
                      {contactField.label}
                      {errors.some(
                        (e) => e.fieldUuid === contactField.uuid
                      ) && (
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          className="text-danger ms-2"
                          title="There are errors"
                        />
                      )}
                    </span>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <span
                      className="mx-2"
                      style={{ cursor: "grab" }}
                      onMouseDown={() => setIsDraggable(true)}
                      onMouseUp={() => setIsDraggable(false)}
                    >
                      <FontAwesomeIcon icon={faHand} />
                    </span>
                    <button
                      type="button"
                      title="Move field up"
                      className="btn btn-link link-secondary"
                      disabled={index === 0}
                      onClick={() => handleUp(index)}
                    >
                      <FontAwesomeIcon icon={faCaretUp} />
                    </button>
                    <button
                      type="button"
                      title="Move field down"
                      className="btn btn-link link-secondary"
                      disabled={index === contactFields.length - 1}
                      onClick={() => handleDown(index)}
                    >
                      <FontAwesomeIcon icon={faCaretDown} />
                    </button>
                    <button
                      type="button"
                      title="Remove field"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleDeleteToggle(contactField.uuid)}
                    >
                      <FontAwesomeIcon icon={faCircleXmark} /> Remove
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <ContactFieldEditor
                    value={contactField}
                    onChange={handleChange}
                  />
                  {isUserError && (
                    <div className="mt-3">
                      {errors.some((e) => e.fieldUuid === contactField.uuid) &&
                        (errors
                          .find((e) => e.fieldUuid === contactField.uuid)
                          ?.messages?.map((m) =>
                            m.startsWith("_options_required_by_sets") ||
                            m.startsWith("_field_required_by_filters") ? (
                              <SelectableContactFieldError
                                key={m}
                                errorMessage={m}
                              />
                            ) : (
                              <div key={m} className="text-danger mb-1">
                                {m}
                              </div>
                            )
                          ) ?? (
                          <div className="text-danger">There are errors</div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleAdd}
          >
            <FontAwesomeIcon icon={faPlus} /> Add contact field
          </button>
          <button
            type="button"
            className="btn btn-primary float-end"
            onClick={handleSave}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSave} /> Save
          </button>
        </div>
      </fieldset>
      <JsonCollapse json={contactFields} />
    </div>
  );
}
